import { Box, Button, Tab, Tabs, Typography } from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { format } from "date-fns";
import { parse } from "date-fns/parse";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FieldValues, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  useBlocker,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { selectAllCities } from "../../../app/appSlice";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { ConfirmationDialog } from "../../../common/components/functional/confirmationDialog/ConfirmationDialog";
import OverlaySpinner from "../../../common/components/functional/overlaySpinner/OverlaySpinner";
import getLocale from "../../../common/helpers/getLocale";
import isEqual from "../../../common/helpers/isEqual";
import { AutocompleteType } from "../../../common/types/autoComplete";
import useMessage from "../../../hooks/useMessage";
import languages from "../../../languages";
import { OrderEdit, OrderType, WorkTitle } from "../../orders/orderAPI";
import { selectOrderRowById } from "../../orders/orderRowsDetailedSlice";
import { selectAllWorkTitles } from "../../workTitles/workTitleSlice";
import {
  closeJobAd,
  createJobAd,
  createJobAdIntegration,
  editJobAd,
  editJobAdIntegration,
  fetchJobAdById,
  publishJobAd,
  reOpenJobAd,
  selectJobAdById,
} from "../jobAdSlice";
import TalentApplicantsStatus from "../jodAdApplicants/TalentApplicantsStatus";
import { clearApplicants } from "../jodAdApplicants/jobAdApplicantsSlice";
import {
  DuunitoriIntegration,
  EezyIntegration,
  EmploymentType,
  JobAd,
  JobAdIntegration,
  JobAdIntegrationType,
  JobAdStatus,
  JoblyIntegration,
  TyomarkkinatoriIntegration,
} from "../types/common";
import JobAdFormActionBanner from "./JobAdFormActionBanner";
import JobAdHeader from "./JobAdHeader";
import DuunitoriSection from "./sections/DuunitoriSection";
import EezySection from "./sections/EezySection";
import GeneralSection from "./sections/GeneralSection";
import JoblySection from "./sections/JoblySection";
import TyomarkkinatoriSection from "./sections/TyomarkkinatoriSection";
import { isJobAdDateRangeValid } from "./dateValidation";
import useIsLoading from "../../../hooks/useIsLoading";
import { isoFormat } from "../../../common/helpers/formatDateTime";

type FormValues = {
  jobAd: JobAd;
  eezyIntegration: EezyIntegration;
  joblyIntegration: JoblyIntegration;
  duunitoriIntegration: DuunitoriIntegration;
  tyomarkkinatoriIntegration: TyomarkkinatoriIntegration;
};

const JobAdForm = () => {
  const { sendMessage } = useMessage();
  const { t } = useTranslation();
  const { jobAdId } = useParams();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("order");
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const locale = getLocale();
  const user = useAppSelector((state) => state.user.user);
  const jobAd = useAppSelector((state) =>
    jobAdId ? selectJobAdById(state, jobAdId) : undefined,
  );
  const order = useAppSelector((state) =>
    orderId ? (selectOrderRowById(state, orderId!!) as OrderEdit) : null,
  );
  const isSliceLoading = useIsLoading(["jobAds"]);

  const [isDataReady, setIsDataReady] = useState<boolean>(false);
  const [selectedJobAd, setSelectedJobAd] = useState<JobAd | undefined>();
  const [isEditing, setIsEditing] = useState<boolean>(!jobAdId);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [integrationsToPublish, setIntegrationsToPublish] = useState<
    JobAdIntegrationType[]
  >([
    JobAdIntegrationType.Eezy,
    JobAdIntegrationType.Jobly,
    JobAdIntegrationType.Duunitori,
    JobAdIntegrationType.Tyomarkkinatori,
  ]);
  const [publishConfirmationOpen, setPublishConfirmationOpen] =
    useState<boolean>(false);

  const [tabIndex, setTabIndex] = useState(0);

  const allCities = useAppSelector(selectAllCities);

  const handleTabChange = (newTabIndex: number) => {
    setTabIndex(newTabIndex);
  };
  const tabs = [
    { label: t("jobAds:jobAd"), value: 0 },
    { label: t("jobAds:statusOfApplicants"), value: 1 },
  ];

  const getLanguageLabel = useCallback(
    (languageCode: string) =>
      locale.code &&
      languages[locale.code].find((i) => i.code === languageCode)?.label,
    [locale],
  );

  const workTitles = useAppSelector((state) => selectAllWorkTitles(state));
  // work titles is not ready when the component is rendered, so we need to get the fieldOfWorkId
  const getFieldOfWorkByWorkTitleId = useCallback(
    (id: string) => {
      return workTitles.find((wt: WorkTitle) => wt.id === id)?.fieldOfWorkId;
    },
    [workTitles],
  );

  const createDefaultValues = (jobAd?: JobAd) => {
    const fieldOfWorkId = order?.taskSpec.workTitleId
      ? getFieldOfWorkByWorkTitleId(order?.taskSpec.workTitleId)
      : jobAd?.fieldOfWorkId || "";

    const integrations = {
      eezyIntegration: jobAd?.integrations?.find(
        (i) => i.integration.type === "Eezy",
      ),
      joblyIntegration: jobAd?.integrations?.find(
        (i) => i.integration.type === "Jobly",
      ),
      tyomarkkinatoriIntegration: jobAd?.integrations?.find(
        (i) => i.integration.type === "Tyomarkkinatori",
      ),
    };

    const eezy = integrations.eezyIntegration?.integration as EezyIntegration;
    const jobly = integrations.joblyIntegration
      ?.integration as JoblyIntegration;
    const tyomarkkinatori = integrations.tyomarkkinatoriIntegration
      ?.integration as TyomarkkinatoriIntegration;
    const orderTypeToEmploymentType = (
      orderType: OrderType | undefined,
    ): EmploymentType | null => {
      if (orderType === OrderType.Continuous) {
        return EmploymentType.INDEFINITE;
      } else if (orderType === OrderType.FixedTerm) {
        return EmploymentType.TEMPORARY;
      } else if (orderType === OrderType.Short) {
        // 2024-10-22 we do not make JobAds from short orders
        return EmploymentType.TEMPORARY;
      } else {
        return null;
      }
    };

    return {
      title: jobAd?.title || "",
      startDate: jobAd?.startDate
        ? parse(jobAd.startDate, isoFormat, new Date())
        : null,
      endDate: jobAd?.endDate
        ? parse(jobAd.endDate, isoFormat, new Date())
        : null,
      locations: order?.workLocationAddress?.city
        ? allCities.filter(
            (option) => option.city === order.workLocationAddress?.city,
          )
        : jobAd?.locations || [],
      jobAdTextFinnish: jobAd?.jobDescription.finnish || "",
      jobAdTextEnglish: jobAd?.jobDescription.english || "",
      contactPersonId: jobAd?.contactPerson.id || user?.id || "",
      contactPersonName:
        jobAd?.contactPerson.name || `${user?.firstName} ${user?.lastName}`,
      contactPersonMail: jobAd?.contactPerson.email || user?.email,
      contactPersonPhone: jobAd?.contactPerson.phone || "",
      fieldOfWorkId: fieldOfWorkId,
      workTitle: order?.taskSpec.workTitleId || jobAd?.workTitleId || "",
      workTimeType: jobAd?.workTimeType || "", //can we fill this from order?
      employmentType:
        orderTypeToEmploymentType(order?.type) || jobAd?.employmentType || "", //can we fill this from order?
      salary: jobAd?.salary || "",
      remoteWork: jobAd && "remoteWork" in jobAd ? jobAd?.remoteWork : null,
      customerCompanyName:
        order?.customerOrgName || jobAd?.customerCompanyName || "",
      hideCustomer: eezy?.hideCustomer || false,
      customerDescription: eezy?.customerDescription || "",
      logo: eezy?.logo || "",
      alternativeApplicationUrl: eezy?.alternativeApplicationUrl || "",
      training: eezy?.training || null,
      campaigns: jobAd?.campaigns
        ? jobAd?.campaigns.map((campaign: string) => ({
            name: campaign,
          }))
        : [],
      temporaryWork: eezy?.temporaryWork,

      jobCategoryJobly: jobly?.jobCategory || "",
      contractTypeJobly: jobly?.contractType || "",
      occupationCategoryJobly: jobly?.occupationCategory || "",
      jobExperienceLevelJobly: jobly?.jobExperienceLevel || "",

      durationOfTemporaryEmployment:
        tyomarkkinatori?.durationOfTemporaryEmployment || "",
      esco: tyomarkkinatori?.ESCO
        ? tyomarkkinatori.ESCO.map((esco: string) => ({ name: esco }))
        : [],
      numberOfJobPositions:
        order?.amount || tyomarkkinatori?.numberOfJobPositions || "",
      salaryType: tyomarkkinatori?.salaryType || "",
      workingLanguages: tyomarkkinatori?.workingLanguages
        ? tyomarkkinatori.workingLanguages.map((languageCode: string) => ({
            id: languageCode,
            name: getLanguageLabel(languageCode),
          }))
        : [],
      assignment: tyomarkkinatori?.assignment || "",
      commissionCompanyName:
        tyomarkkinatori?.commissionCompanyName ||
        jobAd?.customerCompanyName ||
        "",
      jobAdTextTyomarkkinatoriFinnish:
        tyomarkkinatori?.jobDescription?.finnish || "",
      discloseCommissionCompany:
        !jobAd || tyomarkkinatori?.discloseCommissionCompany !== false,
      noteEURES: !jobAd || !!tyomarkkinatori?.noteEURES,
      EURESinstructionsFinnish:
        tyomarkkinatori?.noteEURES?.EURESinstructionsFinnish || "",
      EURESinstructionsEnglish:
        tyomarkkinatori?.noteEURES?.EURESinstructionsEnglish || "",
    };
  };

  const defaultValues = useMemo(() => {
    // Only create default values when both selectedJobAd and cityOptions are ready
    if (selectedJobAd && allCities.length > 0) {
      return createDefaultValues(selectedJobAd);
    }
    return {}; // Return empty object or initial default state if data isn't ready
    // FIXME: fix this or add comment why this is needed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedJobAd, allCities]);

  const methods = useForm({ defaultValues, mode: "onChange" });
  const {
    handleSubmit,
    formState: { isDirty, isValid },
    reset,
    watch,
  } = methods;

  // Main logic for jobAd fetching and setting defaults, waiting until cityOptions are ready
  useEffect(() => {
    const initializeForm = async () => {
      setIsDataReady(false);
      setIsLoading(true);

      // Wait until cityOptions are loaded before proceeding
      if (!allCities.length) return;

      if (jobAdId) {
        // Handle fetching jobAd and setting defaults for existing ads
        dispatch(clearApplicants());

        if (jobAd?.integrations?.length || jobAd?.integrations?.length === 0) {
          setSelectedJobAd(jobAd);
          const defaults = createDefaultValues(jobAd);
          reset({ ...defaults });
        } else {
          const action = await dispatch(fetchJobAdById(jobAdId));
          if (isFulfilled(action)) {
            setSelectedJobAd(action.payload);
            const defaults = createDefaultValues(action.payload);
            reset({ ...defaults });
          }
        }
      }

      // Handle setting defaults for new jobAds when necessary data is available
      else if (workTitles) {
        const defaults = createDefaultValues();
        reset({ ...defaults });
      }

      // All data is ready now
      setIsDataReady(true);
      setIsLoading(false);
    };

    initializeForm();
    // FIXME: fix this or add comment why this is needed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, jobAd, jobAdId, order, workTitles, allCities, reset]);

  useEffect(() => {
    const publishingSites = selectedJobAd?.publishingSites;

    if (publishingSites) {
      const integrations = [];
      for (const integrationType of Object.values(JobAdIntegrationType)) {
        if (publishingSites?.find((i) => i === integrationType)) {
          integrations.push(integrationType);
        }
      }
      setIntegrationsToPublish(integrations);
    }
  }, [selectedJobAd]);

  const saveNewJobAd = (data: FormValues) => {
    dispatch(
      createJobAd({
        ...data.jobAd,
        publishingSites: integrationsToPublish,
      }),
    ).then((action) => {
      if (isFulfilled(action)) {
        handleIntegrations(
          data,
          action.payload.id,
          action.payload.version,
        ).then((saved) => saved && selectedJobAd && setIsEditing(false));
      }
    });
  };

  const updateJobAd = (data: FormValues) => {
    if (!selectedJobAd?.id || selectedJobAd.version === undefined) return;

    const hasChanges =
      Object.keys(selectedJobAd).some((key) => {
        return (
          key in data.jobAd &&
          !isEqual(
            selectedJobAd[key as keyof typeof selectedJobAd],
            data.jobAd[key as keyof typeof selectedJobAd],
          )
        );
      }) || integrationsToPublish !== selectedJobAd?.publishingSites;

    if (!hasChanges) {
      handleIntegrations(data, selectedJobAd.id, selectedJobAd.version).then(
        (saved) => saved && setIsEditing(false),
      );
    } else {
      dispatch(
        editJobAd({
          ...data.jobAd,
          jobAdId: selectedJobAd.id,
          version: selectedJobAd.version,
          publishingSites: integrationsToPublish,
        }),
      ).then((action) => {
        if (isFulfilled(action)) {
          handleIntegrations(
            data,
            action.payload.id,
            action.payload.version,
          ).then((saved) => saved && setIsEditing(false));
        }
      });
    }
  };

  const handleSave = () => {
    if (hasUnsavedChanges()) {
      handleSubmit(onConfirm)();
    } else if (selectedJobAd?.status === JobAdStatus.PUBLISHED) {
      handlePublish();
    } else {
      setIsEditing(false);
    }
  };

  const handlePublish = (version = selectedJobAd?.version ?? undefined) => {
    if (!selectedJobAd?.id || version === undefined) return;

    dispatch(publishJobAd({ id: selectedJobAd.id, version })).then(
      async (action) => {
        if (isFulfilled(action)) {
          setPublishConfirmationOpen(false);
          setIsEditing(false);
          sendMessage(
            t("jobAds:jobAdPublishConfirmation.publishSuccess"),
            "success",
          );
          navigate("/home/jobAds");
        }
      },
    );
  };

  const handleCloseJobAd = async () => {
    if (!selectedJobAd?.id || selectedJobAd.version === undefined) return;

    await dispatch(
      closeJobAd({ id: selectedJobAd.id, version: selectedJobAd.version }),
    ).then(async (action) => {
      if (isFulfilled(action)) {
        sendMessage(t("jobAds:closeJobAdSuccess"), "success");
      }
    });
    navigate("/home/jobAds");
  };

  const handleReOpenJobAd = async () => {
    if (!selectedJobAd?.id || selectedJobAd.version === undefined) return;

    await dispatch(
      reOpenJobAd({ id: selectedJobAd.id, version: selectedJobAd.version }),
    ).then(async (action) => {
      if (isFulfilled(action)) {
        setIsEditing(true);
        sendMessage(t("jobAds:reOpenJobAdSuccess"), "success");
      }
    });
  };

  const handleIntegrations = async (
    data: FieldValues,
    jobAdId: string,
    version: number,
  ) => {
    const promises = [];

    for (const intType of Object.values(JobAdIntegrationType)) {
      if (integrationsToPublish.includes(intType)) {
        const savedIntegration = selectedJobAd?.integrations?.find(
          (i) => i.integration.type === intType,
        );

        if (savedIntegration) {
          const hasChanges = Object.keys(savedIntegration.integration).some(
            (key) => {
              const int = data[intType.toLowerCase() + "Integration"];

              return (
                key in int &&
                !isEqual(
                  savedIntegration.integration[
                    key as keyof typeof savedIntegration.integration
                  ],
                  int[key],
                )
              );
            },
          );

          hasChanges &&
            promises.push(updateIntegration(data, jobAdId, savedIntegration));
        } else {
          promises.push(createIntegration(data, jobAdId, intType));
        }
      }
    }

    const actions = await Promise.all(promises);
    if (actions.some((action) => !isFulfilled(action))) return;

    if (
      selectedJobAd?.status === JobAdStatus.PUBLISHED ||
      selectedJobAd?.status === JobAdStatus.EXPIRED
    ) {
      handlePublish(version);
      // TODO: show confirmation
    } else {
      navigate(`/home/jobAds/${jobAdId}`);
    }
    return true;
  };

  const createIntegration = (
    data: FieldValues,
    jobAdId: string,
    integrationType: JobAdIntegrationType,
  ) => {
    return dispatch(
      createJobAdIntegration({
        ...data[integrationType.toLowerCase() + "Integration"],
        type: integrationType,
        jobAdId: jobAdId,
      }),
    );
  };

  const updateIntegration = (
    data: FieldValues,
    jobAdId: string,
    integration: JobAdIntegration,
  ) => {
    return dispatch(
      editJobAdIntegration({
        id: integration.id || "",
        integration: {
          ...integration.integration,
          ...data[integration.integration.type?.toLowerCase() + "Integration"],
          type: integration.integration.type,
          jobAdId: jobAdId,
        },
      }),
    );
  };

  const hasUnsavedChanges = useCallback(() => {
    return (
      isDirty ||
      integrationsToPublish.toString() !==
        selectedJobAd?.publishingSites?.toString()
    );
  }, [integrationsToPublish, isDirty, selectedJobAd?.publishingSites]);

  const onConfirm = (data: FieldValues) => {
    const newData: FormValues = {
      jobAd: {
        id: selectedJobAd?.id || "",
        bundleId: order?.bundleId,
        title: data.title,
        status: selectedJobAd?.status || JobAdStatus.DRAFT,
        ...(data?.startDate && {
          startDate: format(data.startDate, "yyyy-MM-dd"),
        }),
        endDate: format(data.endDate, "yyyy-MM-dd"),
        locations: data.locations,
        jobDescription: {
          finnish: data.jobAdTextFinnish,
          english: data.jobAdTextEnglish,
        },
        contactPerson: {
          id: data.contactPersonId,
          name: data.contactPersonName,
          email: data.contactPersonMail,
          phone: data.contactPersonPhone,
        },
        fieldOfWorkId: data.fieldOfWorkId,
        workTitleId: data.workTitle.id || data.workTitle,
        workTimeType: data.workTimeType,
        employmentType: data.employmentType,
        salary: data.salary,
        remoteWork: data.remoteWork,
        customerCompanyName: data.customerCompanyName || "",
        campaigns: data.campaigns
          ? data.campaigns.map((campaign: AutocompleteType) => campaign.name)
          : [],
      },
      eezyIntegration: {
        jobAdId: selectedJobAd?.id || "",
        hideCustomer: data.hideCustomer,
        customerDescription:
          (!data.hideCustomer && data.customerDescription) || "",
        logo: (!data.hideCustomer && data.logo) || "",
        alternativeApplicationUrl: data.alternativeApplicationUrl || "",
        training: data.training || false,
        temporaryWork: data.temporaryWork || false,
      },
      joblyIntegration: {
        jobAdId: selectedJobAd?.id || "",
        jobCategory: data.jobCategoryJobly || "",
        contractType: data.contractTypeJobly || "",
        occupationCategory: data.occupationCategoryJobly || "",
        jobExperienceLevel: data.jobExperienceLevelJobly || null,
      },
      duunitoriIntegration: {
        jobAdId: selectedJobAd?.id || "",
      },
      tyomarkkinatoriIntegration: {
        jobAdId: selectedJobAd?.id || "",
        durationOfTemporaryEmployment:
          data.employmentType === EmploymentType.INDEFINITE
            ? ""
            : data.durationOfTemporaryEmployment,
        ESCO: data.esco
          ? data.esco.map((esco: AutocompleteType) => esco.name)
          : [],
        numberOfJobPositions: data.numberOfJobPositions || 0,
        salaryType: data.salaryType || "",
        workingLanguages: data.workingLanguages
          ? data.workingLanguages.map((obj: AutocompleteType) => obj.id)
          : [],
        assignment: data.assignment || "",
        commissionCompanyName: data.commissionCompanyName || "",
        jobDescription: {
          finnish: data.jobAdTextTyomarkkinatoriFinnish || "",
        },
        discloseCommissionCompany: data.discloseCommissionCompany || false,
        noteEURES: data.noteEURES
          ? {
              EURESinstructionsFinnish: data.EURESinstructionsFinnish || "",
              EURESinstructionsEnglish: data.EURESinstructionsEnglish || "",
            }
          : null,
      },
    };
    selectedJobAd ? updateJobAd(newData) : saveNewJobAd(newData);
  };

  const onPublishSelection = (integration: JobAdIntegrationType) => {
    setIntegrationsToPublish((prev) => {
      if (prev.includes(integration)) {
        return prev.filter((i) => i !== integration);
      } else {
        return [...prev, integration];
      }
    });
  };

  const publishConfirmationText = `${t("jobAds:jobAdPublishConfirmation.publishingSitesNote")} (${integrationsToPublish.join(
    ", ",
  )}). ${t("jobAds:jobAdPublishConfirmation.visibilityNote")}`;

  const handleBackClick = () => {
    if (orderId) {
      navigate(`/home/orders/${orderId}`);
    } else {
      navigate("/home/jobAds");
    }
  };

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    return (
      isEditing &&
      hasUnsavedChanges() &&
      currentLocation.pathname !== nextLocation.pathname &&
      !nextLocation.pathname.includes("jobAds/")
    );
  });

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges()) {
        event.preventDefault();
        event.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  if (!isDataReady) {
    return <OverlaySpinner isLoading={true} />; // Show spinner until data is ready
  }

  const watchedForm: any = watch();
  const title = watchedForm?.title || t("jobAds:newJobAd");
  const dateRangeIsValid = isJobAdDateRangeValid(
    watchedForm?.startDate,
    watchedForm?.endDate,
  );

  return (
    <Box pb="70px">
      <OverlaySpinner isLoading={isLoading || isSliceLoading} />
      <JobAdHeader
        title={title}
        isEditing={isEditing}
        jobAdStatus={selectedJobAd?.status}
        handleCloseJobAd={handleCloseJobAd}
        handleDeleteDraft={handleCloseJobAd}
        handleBackClick={handleBackClick}
        handleReOpenJobAd={handleReOpenJobAd}
        hideInfo={!!selectedJobAd || !!order}
      />
      {!!jobAd && (
        <Tabs
          sx={{
            "& .MuiTab-root": {
              textTransform: "capitalize",
              fontWeight: 700,
            },
          }}
          value={tabIndex}
          onChange={(_, value) => handleTabChange(value)}
        >
          {tabs.map((tab) => (
            <Tab
              key={tab.label}
              value={tab.value}
              label={
                <Typography color="textSecondary" variant="header">
                  {tab.label}
                </Typography>
              }
            />
          ))}
        </Tabs>
      )}
      {tabIndex === 0 && (
        <FormProvider {...methods}>
          <Box
            sx={{
              bgColor: "white",
              borderRadius: "12px",
              padding: 2,
            }}
          >
            <GeneralSection
              selectedJobAd={selectedJobAd}
              isEditing={isEditing}
            />
            <EezySection
              selectedJobAd={selectedJobAd}
              isEditing={isEditing}
              integrationsToPublish={integrationsToPublish}
            />
            <DuunitoriSection
              selectedJobAd={selectedJobAd}
              isEditing={isEditing}
              onPublishSelection={onPublishSelection}
              integrationsToPublish={integrationsToPublish}
            />
            <JoblySection
              selectedJobAd={selectedJobAd}
              isEditing={isEditing}
              onPublishSelection={onPublishSelection}
              integrationsToPublish={integrationsToPublish}
            />
            <TyomarkkinatoriSection
              selectedJobAd={selectedJobAd}
              isEditing={isEditing}
              onPublishSelection={onPublishSelection}
              integrationsToPublish={integrationsToPublish}
            />
          </Box>
          <JobAdFormActionBanner
            handleSave={handleSave}
            hasUnsavedChanges={hasUnsavedChanges()}
            jobAdStatus={selectedJobAd?.status}
            onPublishButtonClick={() => setPublishConfirmationOpen(true)}
            isEditing={isEditing}
            setIsEditing={setIsEditing}
            isValid={isValid}
            dateRangeIsValid={dateRangeIsValid}
          />
        </FormProvider>
      )}
      {tabIndex === 1 ? (
        jobAd?.bundleId ? (
          <Box
            sx={{
              width: "478px",
              display: "flex",
              alignItems: "flex-start",
              flexDirection: "column",
              backgroundColor: "#fff",
              padding: "16px",
              borderRadius: "8px",
              boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.1)",
              textAlign: "left",
            }}
          >
            <Typography>
              {t("jobAds:thisJobAdIsConnectedToOrderBundle")}
            </Typography>
            <Button
              variant="contained"
              sx={{
                fontFamily: "Quicksand, sans-serif",
                fontSize: "16px",
                fontWeight: 700,
                height: "42px",
                marginTop: "16px",
                borderRadius: "18px",
                backgroundColor: "primary.main",
                textTransform: "none",
              }}
              onClick={() => navigate(`/home/orders/${jobAd.bundleId}`)}
            >
              {t("jobAds:goToOrderView")}
            </Button>
          </Box>
        ) : (
          <TalentApplicantsStatus jobAdId={jobAd?.id} />
        )
      ) : null}
      <ConfirmationDialog
        title={t("jobAds:jobAdPublishConfirmation.title")}
        contentText={publishConfirmationText}
        confirmText={t("jobAds:jobAdPublishConfirmation.publish")}
        cancelText={t("jobAds:jobAdPublishConfirmation.cancel")}
        isOpen={publishConfirmationOpen}
        onCancel={() => setPublishConfirmationOpen(false)}
        onConfirm={() => handlePublish()}
        isLoading={isLoading || isSliceLoading}
        sx={{ maxWidth: "450px" }}
      />
      {isEditing && blocker.state === "blocked" && (
        <ConfirmationDialog
          title={t("jobAds:unsavedChangesWarningDialog.title")}
          contentText={t("jobAds:unsavedChangesWarningDialog.contentText")}
          confirmText={t("jobAds:unsavedChangesWarningDialog.confirm")}
          cancelText={t("jobAds:jobAdPublishConfirmation.cancel")}
          isOpen={blocker.state === "blocked"}
          onCancel={() => blocker.reset()}
          onConfirm={() => blocker.proceed()}
          isLoading={isLoading || isSliceLoading}
          sx={{ maxWidth: "450px" }}
        />
      )}
    </Box>
  );
};

export default JobAdForm;
